import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWebAnalyticsSites,
  createWebAnalyticsSite,
  updateWebAnalyticsSite,
  deactivateWebAnalyticsSite,
  type CreateWebAnalyticsSiteInput,
  type UpdateWebAnalyticsSiteInput,
} from '../services/api/webAnalyticsSites';

export function useWebAnalyticsSites() {
  return useQuery({
    queryKey: ['webAnalyticsSites'],
    queryFn: getWebAnalyticsSites,
  });
}

// Every mutation invalidates both the registry list (['webAnalyticsSites']) and the
// report's own query namespace (['web-analytics'], shared by every key in
// useWebAnalytics.ts) so an admin who edits the registry and navigates to the report
// does not read a stale client-side cache. The server-side Redis cache is already
// cleared by 85-03's write hook; this covers the TanStack Query layer.

export function useCreateWebAnalyticsSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWebAnalyticsSiteInput) => createWebAnalyticsSite(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webAnalyticsSites'] });
      queryClient.invalidateQueries({ queryKey: ['web-analytics'] });
    },
  });
}

export function useUpdateWebAnalyticsSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateWebAnalyticsSiteInput }) =>
      updateWebAnalyticsSite(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webAnalyticsSites'] });
      queryClient.invalidateQueries({ queryKey: ['web-analytics'] });
    },
  });
}

export function useDeactivateWebAnalyticsSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateWebAnalyticsSite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webAnalyticsSites'] });
      queryClient.invalidateQueries({ queryKey: ['web-analytics'] });
    },
  });
}
